// a library to wrap and simplify api calls
import apisauce from "apisauce"
import AppConfig from "../config/AppConfig"
import routerStore from "../stores/RouterStore"
import Loading from "../utilities/loading"

// our "constructor"
const create = (baseURL = AppConfig.apiURL) => {
  // ------
  // STEP 1
  // ------
  //
  // Create and configure an apisauce-based api object.
  //
  const api = apisauce.create({
    // base URL is read from the "constructor"
    baseURL,
    // here are some default headers
    headers: {
      "Cache-Control": "no-cache",
      Accept: "application/json",
    },
    // 20 second timeout...
    timeout: 50000,
  })
  // api upload
  const apiUpload = apisauce.create({
    // base URL is read from the "constructor"
    baseURL,
    // here are some default headers
    headers: {
      "Cache-Control": "no-cache",
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
    },
    // 20 second timeout...
    timeout: 200000,
  })

  const monitoring = (response) => {
    Loading.hide()
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log(response.config.method, response.config.url, response)
    }
    if (response.status === 401 && response.config.headers.Authorization) {
      routerStore.replace("/login")
    }
  }
  api.addMonitor(monitoring)
  apiUpload.addMonitor(monitoring)

  api.addRequestTransform(() => Loading.show())
  apiUpload.addRequestTransform(() => Loading.show())

  const setToken = (token) => {
    api.setHeader("Authorization", "Bearer " + token)
    apiUpload.setHeader("Authorization", "Bearer " + token)
  }

  // ------
  // STEP 2
  // ------
  //
  // Define some functions that call the api.  The goal is to provide
  // a thin wrapper of the api layer providing nicer feeling functions
  // rather than "get", "post" and friends.
  //
  // I generally don't like wrapping the output at this level because
  // sometimes specific actions need to be take on `403` or `401`, etc.
  //
  // Since we can't hide from that, we embrace it by getting out of the
  // way at this level.
  // auth
  const login = (data) => api.post("/auth/login", data)
  const changePassword = (data) => api.post("/auth/password", data)
  const refreshToken = (refreshToken) => api.post("/auth/refresh", { refresh_token: refreshToken })
  const getCurrentUser = () => api.get("/auth/me")
  const switchUser = (userId) => api.post("/auth/switch-user", { user_id: userId })

  const createClub = (data) => api.post(`/clubs`, data)
  const getClubs = (params) => api.get("/clubs", params)
  const getClub = (id) => api.get(`/clubs/${id}`)
  const getHostEventOverview = (params) => api.get("/host-links/event-overview", params)
  const getHostWeekOverview = (params) => api.get("/host-links/week-overview", params)
  const getReferralRanking = (params) => api.get("/referrals/ranking", params)
  const getReferralDetail = (hostId, params) => api.get(`/referrals/${hostId}`, params)
  const createHostLink = (data) => api.post("/host-links", data)
  const getClubPosts = (id, query) => api.get(`/clubs/${id}/posts`, { query })
  const getClubStories = (id, query) => api.get(`/clubs/${id}/stories`, { query })
  const updateClub = (id, data) => api.put(`/clubs/${id}`, data)
  const deleteClub = (id) => api.delete(`/clubs/${id}`)
  const uploadClubLogo = (id, formData) => apiUpload.post(`/clubs/${id}/upload-logo`, formData)
  const uploadClubMedia = (id, formData) => apiUpload.post(`/clubs/${id}/upload-media`, formData)
  const uploadMenu = (id, formData) => apiUpload.post(`/clubs/${id}/upload-menu`, formData)
  const getClubPositions = (query) => api.get(`/clubs/positions`, query)
  const updateClubPositions = (data) => api.post(`/clubs/positions`, data)

  const getCities = (query) => api.get("/cities", { query })
  const createCity = (data) => api.post("/cities", data)
  const updateCity = (id, data) => api.put(`/cities/${id}`, data)

  const getTutorials = (query) => api.get("/tutorials", { query })
  const createTutorial = (data) => api.post("/tutorials", data)
  const updateTutorial = (id, data) => api.put(`/tutorials/${id}`, data)
  const deleteTutorial = (id) => api.delete(`/tutorials/${id}`)

  const getRewards = (params) => api.get("/rewards", params)
  const createReward = (data) => api.post("/rewards", data)
  const deleteReward = (id) => api.delete(`/rewards/${id}`)
  const getReward = (id) => api.get(`/rewards/${id}`)
  const updateReward = (id, data) => api.put(`/rewards/${id}`, data)
  const updateRewardPositions = (data) => api.post(`/rewards/positions`, data)

  const getRewardRequestByUserId = (id, query) => api.get(`/reward/request/user/${id}`, { query })

  const createUser = (data) => api.post(`/users`, data)
  const getUsers = (params) => api.get("/users", params)
  const getUser = (id) => api.get(`/users/${id}`)
  const getUserCategories = () => api.get(`/users/categories`)
  const getUserPosts = (id, query) => api.get(`/users/${id}/posts`, { query })
  const updateUser = (id, data) => api.put(`/users/${id}`, data)
  const uploadAvatar = (id, formData) => apiUpload.post(`/users/${id}/upload-avatar`, formData)
  const deleteUser = (id) => api.delete(`/users/${id}`)
  const safeDeleteUser = (id) => api.post(`/users/${id}/safe-delete`)

  const getPosts = (params) => api.get("/posts", params)
  const deletePost = (id) => api.delete(`/posts/${id}`)
  const updatePost = (id, data) => api.put(`/posts/${id}`, data)

  const getReports = (params) => api.get("/reports", params)
  const deleteReport = (id) => api.delete(`/reports/${id}`)

  const getSummary = () => api.get("/summary")
  const getClubSummary = (id) => api.get(`/summary/${id}`)
  const getCommissionsSummary = (params) => api.get("/commissions/summary", params)
  const getCommissions = (params) => api.get("/commissions", params)
  const submitOnboarding = (data) => api.post("/onboarding/submit", data)
  const getOnboardingSubmission = (userId) => api.get(`/onboarding/submissions/${userId}`)
  const approveOnboarding = (userId, data) => api.post(`/onboarding/${userId}/approve`, data)
  const rejectOnboarding = (userId, data) => api.post(`/onboarding/${userId}/reject`, data)
  const requestOnboardingInfo = (userId, data) =>
    api.post(`/onboarding/${userId}/request-info`, data)
  const uploadDocument = (formData) => apiUpload.post("/files/document", formData)

  const getReservations = (params) => api.get(`/reservations`, params)
  const updateReservation = (id, data) => api.put(`/reservations/${id}`, data)
  const confirmReservation = (id, status) =>
    api.put(`/reservations/${id}/confirm`, { confirmation_status: status })

  const getScheduleNotifications = (params) => api.get(`schedule-notifications`, params)
  const sendNotification = (data) => api.post(`/notifications`, data)
  const updateScheduleNotification = (id, data) => api.put(`/schedule-notifications/${id}`, data)
  const deleteScheduleNotification = (id) => api.delete(`/schedule-notifications/${id}`)

  const getPoints = (userId) => api.get(`/points/${userId}`)
  const addPoint = (data) => api.post(`/points`, data)

  const getPromotions = (params) => api.get("/promotions", params)
  const addPromotions = (data) => api.post("/promotions", data)
  const updatePromotion = (id, data) => api.put(`/promotions/${id}`, data)
  const deletePromotion = (id) => api.delete(`/promotions/${id}`)

  const generateDeepLink = (data) => api.post(`/utils/generate-deep-link`, data)
  const inviteMember = (data) => api.post(`/members/invite`, data)

  const getInvoicesAdmin = (params) => api.get(`/invoices/admin`, params)
  const markInvoicePaid = (id) => api.post(`/invoices/${id}/mark-paid`)
  const rejectInvoice = (id, data) => api.post(`/invoices/${id}/reject`, data)
  const requestInvoiceInfo = (id, data) => api.post(`/invoices/${id}/request-info`, data)
  const downloadInvoice = (id) => api.get(`/invoices/${id}/download`, {}, { responseType: "blob" })
  const getInvoices = (params) => api.get(`/invoices`, params)
  const generateInvoice = (data) => api.post(`/invoices/generate`, data)
  const resendInvoice = (id) => api.post(`/invoices/${id}/resend`)
  const sendInvoice = (id) => api.post(`/invoices/${id}/send`)
  const getInvoiceEarnings = (params) => api.get(`/invoices/earnings`, params)

  const uploadFiles = (formData) => apiUpload.post(`/files`, formData)
  const createStory = (data) => api.post(`/stories`, data)
  const getStories = (params) => api.get(`/stories`, params)
  const getStory = (id, params) => api.get(`/stories/${id}`, params)
  const updateStory = (id, data) => api.put(`/stories/${id}`, data)
  const deleteStory = (id) => api.delete(`/stories/${id}`)
  const sortStory = (data) => api.post(`/stories/reorder`, data)
  const getStoryViewers = (id) => api.get(`/stories/${id}/viewers`)

  // chat
  const getConversations = (query) => api.get(`/conversations`, query)
  const addConversation = (data) => api.post(`/conversations`, data)
  const getMessages = (id, query) => api.get(`/conversations/${id}/messages`, query)
  const sendMessage = (message) => api.post(`/messages`, message)
  const deleteMessage = (messageId) => api.delete(`/messages/${messageId}`)
  const readConversation = (id) => api.put(`/conversations/${id}/read`)
  const deleteConversation = (id) => api.delete(`/conversations/${id}`)
  const sendTyping = (id) => api.put(`/conversations/${id}/typing`)
  const uploadMessageFiles = (formData) => apiUpload.post(`/messages/upload`, formData)

  // host handover
  const getHandover = (id) => api.get(`/conversations/${id}/handover`)
  const respondHandover = (id, action) =>
    api.put(`/conversations/${id}/handover/respond`, { action })
  const addParticipant = (id, userId, role = "referral_host") =>
    api.post(`/conversations/${id}/participants`, { user_id: userId, role })
  const removeParticipant = (id, userId) =>
    api.delete(`/conversations/${id}/participants/${userId}`)
  const getHostNotes = (id, query) => api.get(`/conversations/${id}/host-notes`, query)

  // ------
  // STEP 3
  // ------
  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  // methods in step 2.
  //
  // Notice we're not returning back th\e `api` created in step 1?  That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    // a list of the API functions from step 2setToken,
    setToken,
    // auth
    login,
    changePassword,
    refreshToken,
    getCurrentUser,

    // club
    createClub,
    getClubs,
    getHostEventOverview,
    getHostWeekOverview,
    getReferralRanking,
    getReferralDetail,
    createHostLink,
    getClub,
    updateClub,
    deleteClub,
    uploadClubLogo,
    uploadClubMedia,
    uploadMenu,
    getClubPosts,
    getClubStories,
    getClubPositions,
    updateClubPositions,

    // city
    getCities,
    createCity,
    updateCity,

    // tutorial
    getTutorials,
    createTutorial,
    updateTutorial,
    deleteTutorial,

    // Rewards
    getRewards,
    createReward,
    deleteReward,
    getReward,
    updateReward,
    updateRewardPositions,

    // RewardRequest
    getRewardRequestByUserId,

    // user
    createUser,
    getUsers,
    getUser,
    getUserCategories,
    updateUser,
    deleteUser,
    safeDeleteUser,
    getUserPosts,
    uploadAvatar,

    getPosts,
    deletePost,
    updatePost,

    getReports,
    deleteReport,

    getSummary,
    getClubSummary,
    getCommissionsSummary,
    getCommissions,
    submitOnboarding,
    uploadDocument,
    getOnboardingSubmission,
    approveOnboarding,
    rejectOnboarding,
    requestOnboardingInfo,

    getReservations,
    updateReservation,
    confirmReservation,

    sendNotification,
    getScheduleNotifications,
    updateScheduleNotification,
    deleteScheduleNotification,

    getPoints,
    addPoint,

    getPromotions,
    addPromotions,
    updatePromotion,
    deletePromotion,

    generateDeepLink,
    inviteMember,
    getInvoicesAdmin,
    markInvoicePaid,
    rejectInvoice,
    requestInvoiceInfo,
    downloadInvoice,
    getInvoices,
    generateInvoice,
    resendInvoice,
    sendInvoice,
    getInvoiceEarnings,
    uploadFiles,
    createStory,
    getStories,
    getStory,
    updateStory,
    deleteStory,
    sortStory,
    getStoryViewers,

    getConversations,
    addConversation,
    getMessages,
    sendMessage,
    deleteMessage,
    readConversation,
    deleteConversation,
    sendTyping,
    uploadMessageFiles,
    switchUser,

    getHandover,
    respondHandover,
    addParticipant,
    removeParticipant,
    getHostNotes,
  }
}

// let's return back our create method as the default.
const Api = {
  create,
}
export default Api
